/**
 * useEntitlements — thin hook over authStore entitlement helpers.
 *
 * Provides reactive access to module-level license caps and the plan-level
 * permission allowlist for consumers such as T20 (read-only UX gates),
 * the dashboard module visibility check, and P9 (role editor permission gating).
 *
 * Usage:
 *   const { getModuleCap, hasModule, isModuleReadonly, isPermissionEnabledByPlan, refreshUser } = useEntitlements();
 *   if (hasModule('reports')) { ... }
 *   if (isModuleReadonly('analytics')) { showBanner(); }
 *   if (!isPermissionEnabledByPlan('form:submit')) { showLockedIndicator(); }
 */

import { useShallow } from 'zustand/shallow';
import { useAuthStore } from '../stores/authStore';
import type { LicensedModule, ModuleCap } from '../stores/authStore';

export interface UseEntitlementsReturn {
  /**
   * Returns the license cap for a module.
   * Absent key in moduleAccess is treated as 'NONE'.
   */
  getModuleCap: (module: LicensedModule) => ModuleCap;
  /** True when the module cap is READ or FULL (i.e. any access granted). */
  hasModule: (module: LicensedModule) => boolean;
  /** True when the module cap is exactly READ (read-only locked). */
  isModuleReadonly: (module: LicensedModule) => boolean;
  /**
   * Returns the raw plan-level permission allowlist from the org's active
   * plan(s), or undefined when the plan carries no allowlist (open-gate).
   *
   * - `undefined` → open-gate; all RBAC-granted permissions are plan-permitted.
   * - `string[]` → allowlist; only listed keys are plan-permitted.
   * - `[]` (empty) → deny-all; no permissions are plan-permitted.
   */
  getEnabledPermissions: () => string[] | undefined;
  /**
   * Returns true if the permission key is plan-permitted.
   *
   * Gate semantics:
   *   - `undefined` enabledPermissions (open-gate) → always true.
   *   - Non-empty array → true only when key is listed.
   *   - Empty array (deny-all) → always false.
   *
   * **P9 role editor API**: call this for every permission row to determine
   * whether to show a "locked by plan" indicator. Example:
   *   `const planLocked = !isPermissionEnabledByPlan(permKey);`
   */
  isPermissionEnabledByPlan: (key: string) => boolean;
  /**
   * Re-fetches /auth/me and updates the store with fresh entitlements.
   * Exposed so that pages responding to a license-change event (T19/UPS-30)
   * can trigger an explicit refresh.
   */
  refreshUser: () => Promise<void>;
}

export function useEntitlements(): UseEntitlementsReturn {
  return useAuthStore(
    useShallow((state) => ({
      getModuleCap: state.getModuleCap,
      hasModule: state.hasModule,
      isModuleReadonly: state.isModuleReadonly,
      getEnabledPermissions: state.getEnabledPermissions,
      isPermissionEnabledByPlan: state.isPermissionEnabledByPlan,
      refreshUser: state.refreshUser,
    })),
  );
}
